import { TranslateService } from "@ngx-translate/core";
import { map, startWith } from "rxjs";

import { ChangeDetectionStrategy, Component, computed, inject, input, resource, signal } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";

import { IOTNET_REPOSITORY } from "../../../pages/iotnet/iotnet-repository.port";
import { COLOR_BLUE, COLOR_LIGHT_BLUE, COLOR_LIGHT_RED, COLOR_RED } from "../../common/line-chart/colors";
import { PERIOD_OFFSETS } from "../../common/line-chart/components/date-picker/date-picker.component";
import { ChartCustomization } from "../../common/line-chart/line-chart-configuration";
import { LineChart, LineChartLines } from "../../common/line-chart/line-chart.component";
import { getDateRange, toLineChartLine } from "../../common/line-chart/line-chart.helpers";

@Component({
  selector: "iotnet-gateway-traffic-chart",
  templateUrl: "iotnet-gateway-traffic-chart.html",
  styleUrls: ["./iotnet-gateway-traffic-chart.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [LineChart],
})
export class IotnetGatewayTrafficChart {
  public readonly customerId = input.required<string>();
  public readonly gatewayId = input.required<string>();

  private readonly iotnet = inject(IOTNET_REPOSITORY);
  private readonly translate = inject(TranslateService);

  protected readonly dateRangeOfInterest = signal<[Date, Date]>([
    new Date(Date.now() - PERIOD_OFFSETS["1D"]),
    new Date(),
  ]);

  private readonly currentLang = toSignal(
    this.translate.onLangChange.pipe(
      map((e) => e.lang),
      startWith(this.translate.getCurrentLang()),
    ),
    { requireSync: true },
  );

  protected readonly trafficMetadata = computed(() => {
    this.currentLang(); // re-evaluate the labels on language change
    const label = (key: string) => this.translate.instant(key) as string;
    // Peaks start hidden to keep the small chart readable; the legend can re-enable them.
    return {
      peakIn: { color: COLOR_LIGHT_BLUE, label: label("Chart.Label.PeakIn"), visible: false },
      peakOut: { color: COLOR_LIGHT_RED, label: label("Chart.Label.PeakOut"), visible: false },
      in: { color: COLOR_BLUE, label: label("Chart.Label.In") },
      out: { color: COLOR_RED, label: label("Chart.Label.Out"), visible: false },
    };
  });

  // In-traffic is drawn downwards (negated), so tooltips and the y-axis must
  // show absolute values. Sized down to fit the narrow overlay.
  protected readonly customization = computed<ChartCustomization>(() => {
    const metadata = this.trafficMetadata();
    return {
      formatPoint(series: string, y: number) {
        const value = `${Math.abs(Math.round(y)).toLocaleString()} pps`;
        const { color, label } = metadata[series as keyof typeof metadata];
        return `<span style="color: ${color}">\u25CF</span> ${label}: <b>${value}</b><br/>`;
      },
      formatYAxisLabel: (value: number) => Math.abs(value).toLocaleString(),
      height: 240,
      spacing: [16, 12, 8, 12],
      yTickAmount: 5,
    };
  });

  protected readonly traffic = resource<
    LineChartLines,
    { customer_id: string; gateway_id: string; range: [Date, Date] }
  >({
    params: () => ({
      customer_id: this.customerId(),
      gateway_id: this.gatewayId(),
      range: this.dateRangeOfInterest(),
    }),
    loader: async ({ params }) => {
      const { start, end, step } = getDateRange(params.range);
      const traffic = await this.iotnet.getIotnetGatewayTraffic({
        customer_id: params.customer_id,
        gateway_id: params.gateway_id,
        start,
        end,
        step,
      });
      return {
        peakIn: toLineChartLine(traffic.peakIn, -1),
        peakOut: toLineChartLine(traffic.peakOut),
        in: toLineChartLine(traffic.in, -1),
        out: toLineChartLine(traffic.out),
      };
    },
    defaultValue: { peakIn: [], peakOut: [], in: [], out: [] },
  });
}
